/*
 * Spreetail Excel Add-in Commands
 * Handles ribbon button actions for Amazon sales data management
 */

/* global Office, Excel */

import {
  fetchFilteredAdvertisingData,
  fetchMockMarketingData,
  MockProduct,
  mockSalesData,
} from "../shared/mockData";
import { createEnhancedSalesPerformanceDashboardWithParams } from "../taskpane/dashboard";
import { generateInventoryDashboard } from "../taskpane/inventory";
import {
  generateSalesDashboard,
  isAdvertisingDataSheet,
  refreshAdvertisingRawDataSheet,
  updateCopiedSheet,
} from "../taskpane/rowdata";
import { updateCopiedAdvertisingSheet } from "../taskpane/advertisingRowdata";
import { DEFAULT_PROMPT } from "../shared/constants";
import {
  STORAGE_KEYS,
  FILTER_UI_SOURCES,
  CUSTOM_PROPERTIES,
  DASHBOARD_TYPES,
  DASHBOARD_NAMES,
  SHEET_NAMES,
  SHEET_PREFIXES,
  SHEET_PATTERNS,
  SHEET_TYPE_KEYWORDS,
  DIALOG_SIZES,
} from "../shared/constants";
import { generateMarketingDashboard } from "../taskpane/marketing";
import { AdvertisingFilterState, updateAdvertisingFilterStateForSheet, filterStateManager, getAdvertisingFilterState } from "../shared/filterStateManager";
// import { ConnectionStatus } from "../taskpane/components/dashboard/DashboardControls";
import { extractSelectedData, extractSKUs } from "../shared/selectedDataExtractor";
import { extractPivotTableDataIfPresent } from "../shared/pivotTableDataExtractor";

/**
 * Creates a dashboard-style heading at the top of a worksheet with 3 rows, copying Sales Dashboard header style
 * @param sheet - The Excel worksheet
 * @param headingText - The text to display in the heading
 * @param context - Excel context for syncing
 * @param dashboardType - Type of dashboard ("Sales", "Inventory", "Raw Data")
 * @param dateRange - Optional date range to display in the header
 * @param startPurchaseDate - Start date in ISO format for editable inputs
 * @param endPurchaseDate - End date in ISO format for editable inputs
 */

async function createDashboardHeading(
  sheet: Excel.Worksheet,
  headingText: string,
  context: Excel.RequestContext,
  dashboardType: string = DASHBOARD_TYPES.RAW_DATA,
  _dateRange?: string,
  startPurchaseDate?: string,
  endPurchaseDate?: string
) {
  // Helper function to format date for display (copied from Sales Dashboard)
  const formatDateForDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ============ TITLE + INLINE DATE INPUTS SECTION ============
  // Header background band (extend across entire title area) - copied from Sales Dashboard
  const headerBandRange = sheet.getRange("A1:K2");
  headerBandRange.format.fill.color = "#F0F4F8";
  headerBandRange.format.verticalAlignment = "Center";

  // Title area - adapted for Data sheets; Advertising Raw Data should start at A1
  const isAdvertisingHeading = headingText === SHEET_NAMES.ADVERTISING_RAW_DATA;
  const titleAddress = isAdvertisingHeading ? "A1:D2" : "C1:F2";
  const titleCell = isAdvertisingHeading ? "A1" : "C1";
  const titleTextRange = sheet.getRange(titleAddress);
  titleTextRange.merge();

  // Replace "Dashboard" with "Data" in the heading
  let displayHeading = headingText;
  if (headingText.includes(SHEET_TYPE_KEYWORDS.DASHBOARD)) {
    displayHeading = headingText.replace(SHEET_TYPE_KEYWORDS.DASHBOARD, SHEET_TYPE_KEYWORDS.DATA);
  } else if (!headingText.includes(SHEET_TYPE_KEYWORDS.DATA)) {
    displayHeading = `${headingText} Data`;
  }

  sheet.getRange(titleCell).values = [[displayHeading]];
  titleTextRange.format.font.size = 16;
  titleTextRange.format.font.bold = true;
  titleTextRange.format.font.color = "#1F4788";
  titleTextRange.format.horizontalAlignment = "Left";
  titleTextRange.format.fill.color = "#F0F4F8";

  // Nudge title for non-Advertising only; Advertising should start at A with no indent
  try {
    if (!isAdvertisingHeading) {
      (titleTextRange.format as any).indentLevel = 1;
    }
  } catch (e) {
    /* not critical */
  }

  // ============ THIRD ROW FOR FILTER INFORMATION ============
  const thirdRowRange = sheet.getRange("A3:K3");
  thirdRowRange.format.fill.color = "#F0F4F8";

  // Skip editable date inputs for Raw Data type
  if (dashboardType !== DASHBOARD_TYPES.RAW_DATA) {
    // Inline date inputs moved to third row: Start (I3), hyphen (J3), End (K3) - exact copy from Sales Dashboard
    const startDateCell = sheet.getRange("I3");
    const hyphenCell = sheet.getRange("J3");
    const endDateCell = sheet.getRange("K3");

    // Check if we have date range data to display
    const shouldShowDateRange = startPurchaseDate && endPurchaseDate;

    // Write incoming dates to the input cells (display format only)
    const displayStartDate = shouldShowDateRange ? formatDateForDisplay(startPurchaseDate) : "";
    const displayEndDate = shouldShowDateRange ? formatDateForDisplay(endPurchaseDate) : "";

    console.log("Setting Data sheet date cells with:", {
      displayStartDate,
      displayEndDate,
      startPurchaseDate,
      endPurchaseDate,
    });

    startDateCell.values = [[displayStartDate]];
    endDateCell.values = [[displayEndDate]];
    hyphenCell.values = [["-"]];

    // Styling for minimal, sleek inputs - exact copy from Sales Dashboard
    [startDateCell, endDateCell].forEach((cell) => {
      cell.format.font.size = 11;
      cell.format.font.color = "#1F4788";
      cell.format.horizontalAlignment = "Left";
      cell.format.verticalAlignment = "Center";
      cell.numberFormat = [["mmm d, yyyy"]];
      // Minimal underline style to suggest input without heavy borders
      const bottomBorder = cell.format.borders.getItem("EdgeBottom");
      bottomBorder.style = "Continuous";
      bottomBorder.color = "#C5D1E6";
      bottomBorder.weight = Excel.BorderWeight.hairline;
      // Clear the other edges to stay borderless
      ["EdgeTop", "EdgeLeft", "EdgeRight"].forEach((edge) => {
        const b = cell.format.borders.getItem(edge as Excel.BorderIndex);
        b.weight = Excel.BorderWeight.hairline;
        b.color = "#F0F4F8";
      });
      // Light fill to blend with header
      cell.format.fill.color = "#F0F4F8";
    });

    hyphenCell.format.font.size = 12;
    hyphenCell.format.font.color = "#1F4788";
    hyphenCell.format.horizontalAlignment = "Center";
    hyphenCell.format.verticalAlignment = "Center";
    hyphenCell.format.fill.color = "#F0F4F8";

    // Set appropriate column widths for date input columns
    try {
      sheet.getRange("I:I").format.columnWidth = 90; // Start date column
      sheet.getRange("J:J").format.columnWidth = 30; // Hyphen column
      sheet.getRange("K:K").format.columnWidth = 90; // End date column
    } catch (e) {
      console.warn("Could not set date column widths:", e);
    }
  }

  // Handle SKU filtering display for Sales and Inventory types
  if (dashboardType === DASHBOARD_TYPES.SALES || dashboardType === DASHBOARD_TYPES.INVENTORY) {
    // Get filter parameters from storage
    const filterParams = await OfficeRuntime.storage.getItem(STORAGE_KEYS.DASH_FILTER);

    if (filterParams) {
      const params = JSON.parse(filterParams);

      // Display filtered SKUs centered below the dashboard heading (B3:F3)
      if (params.skus && params.skus.length > 0) {
        const skuText = params.skus.join(", ");
        const filterText = `Filtered SKUs: ${skuText}`;

        // Display filtered SKUs centered in B3:F3 below the dashboard heading
        const centeredSkuRange = sheet.getRange("B3:F3");
        centeredSkuRange.merge();
        sheet.getRange("B3").values = [[filterText]];
        centeredSkuRange.format.font.size = 10;
        centeredSkuRange.format.font.italic = true;
        centeredSkuRange.format.font.color = "#666666";
        centeredSkuRange.format.horizontalAlignment = "Center";
        centeredSkuRange.format.verticalAlignment = "Center";
        centeredSkuRange.format.fill.color = "#F0F4F8";
      } else {
        // Clear the cells if no SKUs are filtered
        try {
          sheet.getRange("B3:F3").unmerge();
        } catch (e) {
          /* ignore if not merged */
        }
        sheet.getRange("B3:F3").clear(Excel.ClearApplyTo.contents);
      }
    }
  }

  await context.sync();
}

// Function to save default prompt to history
async function saveDefaultPromptToHistory(): Promise<void> {
  try {
    // Skip saving in commands context - the API will ensure default prompt exists
    // when prompts are saved from the taskpane. This avoids webpack module loading issues.
    return;
  } catch (error) {
    // Fail silently - this is not critical
    console.debug("Could not save default prompt to history (non-critical):", error);
  }
}

/**
 * Reads date range from Dashboard sheet cells I3 and K3
 * @param context Excel context
 * @returns formatted date range string or null if not found
 */
async function readDateRangeFromDashboard(context: Excel.RequestContext): Promise<string | null> {
  try {
    // Check if Dashboard sheet exists
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const dashboardSheet = worksheets.items.find((sheet) => sheet.name === SHEET_NAMES.DASHBOARD);
    if (!dashboardSheet) {
      return null;
    }

    // Read date cells from Dashboard
    const startCell = dashboardSheet.getRange("I3");
    const endCell = dashboardSheet.getRange("K3");

    startCell.load(["values", "text"]);
    endCell.load(["values", "text"]);
    await context.sync();

    // Try to get the actual displayed text first, fallback to values
    let startText = "";
    let endText = "";

    try {
      startText =
        startCell.text && startCell.text[0] && startCell.text[0][0]
          ? String(startCell.text[0][0])
          : "";
      if (!startText) {
        startText =
          startCell.values && startCell.values[0] && startCell.values[0][0]
            ? String(startCell.values[0][0])
            : "";
      }
    } catch (e) {
      startText =
        startCell.values && startCell.values[0] && startCell.values[0][0]
          ? String(startCell.values[0][0])
          : "";
    }

    try {
      endText =
        endCell.text && endCell.text[0] && endCell.text[0][0] ? String(endCell.text[0][0]) : "";
      if (!endText) {
        endText =
          endCell.values && endCell.values[0] && endCell.values[0][0]
            ? String(endCell.values[0][0])
            : "";
      }
    } catch (e) {
      endText =
        endCell.values && endCell.values[0] && endCell.values[0][0]
          ? String(endCell.values[0][0])
          : "";
    }

    // Format dates if both are available
    if (startText && endText && startText !== "undefined" && endText !== "undefined") {
      // Parse and format the dates
      const formatDate = (dateStr: string) => {
        try {
          const num = parseFloat(dateStr);
          if (!isNaN(num) && num > 10000) {
            // Convert Excel serial number to date
            const excelEpoch = new Date(1900, 0, 1);
            const date = new Date(excelEpoch.getTime() + (num - 2) * 24 * 60 * 60 * 1000);
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
          }

          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
          }
        } catch (e) {
          // Return original string if parsing fails
        }
        return dateStr;
      };

      const formattedStart = formatDate(startText);
      const formattedEnd = formatDate(endText);
      return `${formattedStart} - ${formattedEnd}`;
    }

    return null;
  } catch (error) {
    console.error("Error reading date range from Dashboard:", error);
    return null;
  }
}

// Initialize Office.js
Office.onReady(() => {
  console.log("Office.js is ready in commands.ts");
  console.log("Platform------", Office.context.platform);

  // Register all the command functions across all platforms (including Excel on the web)
  console.log("Registering command functions...");

  // Register all functions
  Office.actions.associate("refreshAmazonData", refreshAmazonData);
  Office.actions.associate("createSalesPerformanceDashboard", createSalesPerformanceDashboard);
  Office.actions.associate("createMarketingDashboard", createMarketingDashboard);
  Office.actions.associate("smartMatch", smartMatch);
  Office.actions.associate("smartColumn", smartColumn);
  Office.actions.associate("aggregation", aggregation);
  Office.actions.associate("rootCauseAnalysis", rootCauseAnalysis);
  Office.actions.associate("createMarketWatchDashboard", createMarketWatchDashboard);
  // Office.actions.associate("openCampaignPerformance", openCampaignPerformance);
  // Office.actions.associate("openKeywordIntelligence", openKeywordIntelligence);
  // Office.actions.associate("openAttributionAnalysis", openAttributionAnalysis);
  Office.actions.associate("showRawDataPreserve", showRawDataPreserve);
  Office.actions.associate("showRawData", showRawData);
  Office.actions.associate("refreshDataPlaceholder", refreshDataPlaceholder);
  Office.actions.associate("showInsights", showInsights);
  Office.actions.associate("closeInsightsModal", closeInsightsModal);
  Office.actions.associate("loadData", loadData);
  Office.actions.associate("saveDataDefault", saveDataDefault);
  Office.actions.associate("saveData", saveData);
  Office.actions.associate("modifyPrompt", modifyPrompt);

  console.log("All command functions registered successfully");
});

/**
 * Writes large 2D data arrays to a worksheet in smaller chunks to avoid
 * payload limits on Excel Web (common on Mac/browser).
 */
async function writeValuesInChunks(
  sheet: Excel.Worksheet,
  startRow: number,
  columnsLength: number,
  data: any[][],
  context: Excel.RequestContext,
  chunkSize: number = 500
) {
  const lastCol = getExcelColumnLetter(columnsLength);
  for (let i = 0; i < data.length; i += chunkSize) {
    const chunk = data.slice(i, i + chunkSize);
    const from = startRow + i;
    const to = from + chunk.length - 1;
    const range = sheet.getRange(`A${from}:${lastCol}${to}`);
    range.values = chunk;
    // Sync periodically to keep payload sizes small
    await context.sync();
  }
}

/**
 * Refreshes Amazon sales data directly into Excel without opening the task pane
 * @param event
 */

async function isAuthorized() {
  const token = await OfficeRuntime.storage.getItem(STORAGE_KEYS.TOKEN);
  return token ? true : false;
}

async function refreshAmazonData(event: Office.AddinCommands.Event) {
  try {
    const userAuthorized = await isAuthorized();
    if (!userAuthorized) {
      event.completed();
      return;
    }

    await Excel.run(async (context) => {
      // Get the active worksheet
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      // Clear existing data starting from A1
      const range = sheet.getRange("A1:K50");
      range.clear();

      // Add headers
      const headers = [
        [
          "SKU",
          "ASIN",
          "Product Name",
          "Category",
          "Revenue",
          "Units",
          "Velocity",
          "Growth %",
          "Status",
          "Last Sale",
          "AOV",
        ],
      ];

      const headerRange = sheet.getRange("A1:K1");
      headerRange.values = headers;
      headerRange.format.fill.color = "#4472C4";
      headerRange.format.font.color = "white";
      headerRange.format.font.bold = true;

      // Add data
      const dataValues = mockSalesData.products.map((item) => [
        item.SKU,
        item.ASIN,
        item.ProductName,
        item.Category,
        item.Revenue,
        item.Units,
        item.Velocity,
        item.Growth,
        item.Status,
        item.LastSale,
        item.AOV,
      ]);

      const dataRange = sheet.getRange(`A2:K${dataValues.length + 1}`);
      dataRange.values = dataValues;

      // Format the table
      const tableRange = sheet.getRange(`A1:K${dataValues.length + 1}`);
      const table = sheet.tables.add(tableRange, true);
      table.style = "TableStyleMedium2";

      // Auto-fit columns
      sheet.getUsedRange().format.autofitColumns();

      await context.sync();

      // Show success message
      Office.context.ui.displayDialogAsync(
        `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h2 style="color: #28a745;">✓ Data Refreshed Successfully</h2>
            <p>Amazon sales data has been updated with ${dataValues.length} products.</p>
            <p style="font-size: 12px; color: #666;">Last updated: ${new Date().toLocaleString()}</p>
            <button onclick="Office.context.ui.messageParent('close')" 
                    style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
        DIALOG_SIZES.SMALL
      );
    });
  } catch (error) {
    console.error("Error refreshing data:", error);
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #dc3545;">⚠ Error</h2>
          <p>Failed to refresh Amazon data. Please try again.</p>
          <button onclick="Office.context.ui.messageParent('close')"
                  style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  }

  // Signal that the command is complete
  event.completed();
}

/**
 * Creates a comprehensive Sales Performance Dashboard in Excel
 * This function displays KPIs, summary data, and product table directly in Excel cells
 */
async function createSalesPerformanceDashboard(event: Office.AddinCommands.Event) {
  try {
    const userAuthorized = await isAuthorized();
    if (!userAuthorized) {
      event.completed();
      return;
    }

    console.log("🎯 Ribbon: Step 1 - Generating Sales Dashboard with full dataset...");
    // Signal taskpane to show global loader
    try {
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.SALES);
    } catch (_) { }

    // First, create the dashboard with full dataset (no filters)
    await createEnhancedSalesPerformanceDashboardWithParams({
      skus: [],
      start_purchase_date: "",
      end_purchase_date: "",
    });

    console.log("✅ Ribbon: Step 2 - Dashboard created, navigating to filter UI...");

    // Then navigate to the filter UI in the taskpane (after generation)
    window.location.hash = "#/sales-filter";
    await Office.addin.showAsTaskpane();

    console.log("✅ Ribbon: Filter UI opened successfully");
  } catch (error) {
    console.error("❌ Error creating Sales Performance Dashboard:", error);
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #dc3545;">⚠ Error</h2>
          <p>Failed to create Sales Performance Dashboard. Please try again.</p>
          <button onclick="Office.context.ui.messageParent('close')" 
                  style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  }

  // Clear loader
  try {
    await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
    await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
  } catch (_) { }

  event.completed();
}

/**
 * Creates the Inventory Dashboard in Excel
 * @param event
 */
async function createInventoryDashboard(event: Office.AddinCommands.Event) {
  try {
    await generateInventoryDashboard();
  } catch (error) {
    console.error("Error creating Inventory Dashboard:", error);
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #dc3545;">⚠ Error</h2>
          <p>Failed to create Inventory Dashboard. Please try again.</p>
          <button onclick="Office.context.ui.messageParent('close')" 
                  style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  }

  // Signal that the command is complete
  event.completed();
}

async function geneateDefultRawData(context: Excel.RequestContext) {
  // Get or create the Raw Data worksheet
  let rawDataSheet: Excel.Worksheet;
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  // Check if Raw Data worksheet exists and delete it
  let rawDataExists = false;
  worksheets.items.forEach((sheet) => {
    if (sheet.name === SHEET_NAMES.RAW_DATA) {
      rawDataExists = true;
      rawDataSheet = sheet;
    }
  });

  if (rawDataExists) {
    console.log("📝 Raw Data worksheet exists, deleting...");
    rawDataSheet!.delete();
    await context.sync();
  }

  console.log("➕ Creating new Raw Data worksheet...");
  rawDataSheet = worksheets.add(SHEET_NAMES.RAW_DATA);
  rawDataSheet.activate();
  await context.sync();

  // Add dashboard-style heading with empty date inputs for default raw data
  await createDashboardHeading(
    rawDataSheet,
    "Raw Data Sheet",
    context,
    DASHBOARD_TYPES.RAW_DATA,
    null,
    null,
    null
  );

  // Fetch and display raw data
  const { fetchMockSalesData, fetchMockProductData } = await import("../shared/mockData");
  const salesDataResponse = await fetchMockSalesData();
  const productData: { products: MockProduct[] } = await fetchMockProductData();
  await context.sync();
  // --- PRODUCTS GRID ---
  // Add product headers below the heading with a blank row in between
  const productHeaders = [["SKU", "ASIN", "Product Name", "Category", "Cost per unit"]];
  const productHeaderRange = rawDataSheet.getRange("A4:E4"); // Start after heading (rows 1-2) and blank row (row 3)
  productHeaderRange.values = productHeaders;
  productHeaderRange.format.fill.color = "#4472C4";
  productHeaderRange.format.font.color = "white";
  productHeaderRange.format.font.bold = true;

  // Prepare product data rows using MockProduct type, only main attributes
  const productRows = productData.products.map((product: MockProduct) => {
    const { SKU, ASIN, Name, Category, ["Cost per unit"]: costPerUnit } = product;
    return [SKU || "", ASIN || "", Name || "", Category || "", costPerUnit || ""];
  });

  await context.sync();

  // Add product data rows
  if (productRows.length > 0) {
    const productDataRange = rawDataSheet.getRange(`A5:E${4 + productRows.length}`);
    productDataRange.values = productRows;

    // Format cost per unit as currency
    const costColRange = rawDataSheet.getRange(`E5:E${4 + productRows.length}`);
    costColRange.numberFormat = [["$#,##0.00"]];

    // Auto-fit columns for product grid
    rawDataSheet.getRange(`A4:E${4 + productRows.length}`).format.autofitColumns();
  }

  // --- ORDERS GRID ---
  // Use raw columnar data from API only
  if (!salesDataResponse.columns || !salesDataResponse.data) {
    throw new Error("Invalid API response: missing columns or data");
  }

  const columns = salesDataResponse.columns;
  const dataRows = salesDataResponse.data;

  // Add orders headers below the products grid, with a blank row in between
  const orderStartRow = productRows.length + 6; // Account for heading (2 rows), blank row (1), product header (1), product data (productRows.length), blank row (1)
  const lastCol = getExcelColumnLetter(columns.length);

  // Set headers from API response
  const orderHeaders = [columns];
  const orderHeaderRange = rawDataSheet.getRange(`A${orderStartRow}:${lastCol}${orderStartRow}`);
  orderHeaderRange.values = orderHeaders;
  orderHeaderRange.format.fill.color = "#4472C4";
  orderHeaderRange.format.font.color = "white";
  orderHeaderRange.format.font.bold = true;

  await context.sync();
  // Add order data rows
  if (dataRows.length > 0) {
    const orderDataRange = rawDataSheet.getRange(
      `A${orderStartRow + 1}:${lastCol}${orderStartRow + dataRows.length}`
    );
    orderDataRange.values = dataRows;

    // Format currency columns if they exist
    const salePriceIndex = columns.indexOf("sale_price");
    if (salePriceIndex >= 0) {
      const salePriceCol = getExcelColumnLetter(salePriceIndex + 1);
      const amountRange = rawDataSheet.getRange(
        `${salePriceCol}${orderStartRow + 1}:${salePriceCol}${orderStartRow + dataRows.length}`
      );
      amountRange.numberFormat = [["$#,##0.00"]];
    }

    // Auto-fit columns
    rawDataSheet
      .getRange(`A${orderStartRow}:${lastCol}${orderStartRow + dataRows.length}`)
      .format.autofitColumns();
  }

  await context.sync();
  // Show success message
  Office.context.ui.displayDialogAsync(
    `data:text/html,
    <html>
      <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
        <h2 style="color: #28a745;">✓ Raw Data Displayed</h2>
        <p>Raw sales data and product data have been loaded into the "Raw Data" worksheet.</p>
        <p style="font-size: 12px; color: #666;">
          ${dataRows.length} orders and ${productRows.length} products loaded successfully
        </p>
        <button onclick="Office.context.ui.messageParent('close')" 
                style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Close
        </button>
      </body>
    </html>`,
    { height: 200, width: 350 }
  );
}

function generateCatchRawData(error: any) {
  return Office.context.ui.displayDialogAsync(
    `data:text/html,
    <html>
      <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
        <h2 style="color: #dc3545;">⚠ Error</h2>
        <p>Failed to display raw data. Please try again.</p>
        <p style="font-size: 12px; color: #666;">${error.message}</p>
        <button onclick="Office.context.ui.messageParent('close')"
                style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
          Close
        </button>
      </body>
    </html>`,
    DIALOG_SIZES.SMALL
  );
}

async function findActiveSheet(context: Excel.RequestContext) {
  const sheet = context.workbook.worksheets.getActiveWorksheet();
  sheet.load("name");
  await context.sync();
  return sheet;
}

async function findSalesDataSheet(context: Excel.RequestContext) {
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();
  const items = worksheets.items;
  const regex = SHEET_PATTERNS.SALES_DATA_GLOBAL;
  const rowData = items.find((row) => row.name.match(regex));
  return rowData;
}

function generateNewSheetName(existingName: string, worksheets: Excel.Worksheet[]) {
  console.log("existingName", existingName);
  let newSheetName = `${existingName} - Copy`;
  let counter = 1;
  const existingNames = worksheets.map((ws) => ws.name);
  console.log("existingNames", existingNames);
  while (existingNames.includes(newSheetName)) {
    newSheetName = `${existingName} - Copy (${counter++})`;
  }
  console.log("newSheetName", newSheetName);
  return newSheetName;
}

// Converts a 1-based column number to its Excel column letters (e.g., 1 -> A, 26 -> Z, 27 -> AA)
function getExcelColumnLetter(columnNumber: number): string {
  let n = Math.max(1, Math.floor(columnNumber));
  let columnName = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    columnName = String.fromCharCode(65 + rem) + columnName;
    n = Math.floor((n - 1) / 26);
  }
  return columnName;
}

/**
 * Shows products data and orders data as separate grids in a dedicated worksheet
 * Context-aware: Shows appropriate raw data based on active dashboard or no-dashboard page
 * @param event
 */
async function showRawData(event: Office.AddinCommands.Event) {
  const userAuthorized = await isAuthorized();
  if (!userAuthorized) {
    event.completed();
    return;
  }

  try {
    await Excel.run(async (context) => {
      console.log("📊 Starting context-aware raw data display...");

      const sheet = await findActiveSheet(context);
      const sheetName = sheet.name;
      console.log("Active sheet:", sheetName);

      // Determine dashboard type based on sheet name
      let dashboardType: string | null = null;
      let dateRange: string | null = null;

      if (sheetName === SHEET_NAMES.DASHBOARD) {
        dashboardType = DASHBOARD_TYPES.SALES;
        dateRange = await readDateRangeFromDashboard(context);
        console.log("Sales Dashboard detected, date range:", dateRange);
      } else if (sheetName === DASHBOARD_TYPES.INVENTORY) {
        dashboardType = DASHBOARD_TYPES.INVENTORY;
        console.log("Inventory Dashboard detected");
      } else if (
        sheetName === DASHBOARD_TYPES.MARKETING ||
        sheetName === DASHBOARD_TYPES.ADVERTISING ||
        sheetName === SHEET_TYPE_KEYWORDS.ADVERTISING_DASHBOARD ||
        sheetName.includes(SHEET_TYPE_KEYWORDS.ADVERTISING)
      ) {
        dashboardType = DASHBOARD_TYPES.ADVERTISING;
        console.log("Advertising Dashboard detected");
      } else if (sheetName.startsWith(SHEET_PREFIXES.MW)) {
        dashboardType = "MarketWatch";
        console.log("Market Watch sheet detected");
      } else {
        // Check if it's a raw data sheet
        const { isRawDataSheet } = await import("../taskpane/rowdata");
        if (isRawDataSheet(sheetName)) {
          // If we're on a raw data sheet, determine type from sheet name
          if (sheetName.includes(SHEET_TYPE_KEYWORDS.SALES)) {
            dashboardType = DASHBOARD_TYPES.SALES;
          } else if (sheetName.includes(SHEET_TYPE_KEYWORDS.INVENTORY)) {
            dashboardType = DASHBOARD_TYPES.INVENTORY;
          } else if (sheetName.includes(SHEET_TYPE_KEYWORDS.ADVERTISING)) {
            dashboardType = DASHBOARD_TYPES.ADVERTISING;
          }
          console.log("Raw data sheet detected, type:", dashboardType);
        } else {
          // No active dashboard - show no-dashboard page
          console.log("No active dashboard detected, showing no-dashboard page");
          // window.location.hash = "#/no-active-dashboard";
          await Office.addin.showAsTaskpane();
          event.completed();
          return;
        }
      }

      // Handle each dashboard type
      switch (dashboardType) {
        case DASHBOARD_TYPES.SALES:
          await handleSalesRawData(context, dateRange);
          try { await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DATA); } catch (_) { }
          window.location.hash = "#/sales-filter";
          await Office.addin.showAsTaskpane();
          break;
        case DASHBOARD_TYPES.INVENTORY:
          await handleInventoryRawData(context);
          break;
        case "MarketWatch": {
          const { loadFiltersFromActiveMWSheet, saveMarketWatchFilters } =
            await import("../shared/marketWatchFilterState");
          const { extractMWTimestamp } = await import("../shared/marketWatchPipeline");
          const { fetchMarketWatchData } = await import("../shared/apiService");
          const { createDataSheet, generateTimestamp, MARKET_WATCH_SHEET } =
            await import("../taskpane/components/Market-Watch");

          // Show loader UI
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.MARKET_WATCH_DATA);
          } catch (_) {}

          // Hydrate filters from active MW sheet
          const mwFilters = await loadFiltersFromActiveMWSheet();
          if (
            !mwFilters ||
            (mwFilters.asin.length === 0 &&
              mwFilters.keyword.length === 0 &&
              mwFilters.brand.length === 0)
          ) {
            // No filters found — open taskpane so user can configure
            console.log("MW Fetch: No filters found on active sheet, opening taskpane");
            window.location.hash = "#/market-watch";
            await Office.addin.showAsTaskpane();
            try {
              await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
              await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
            } catch (_) {}
            break;
          }

          // Fetch = data-only: re-fetch data and replace only the MW Raw Data sheet
          const mwFetchData = await fetchMarketWatchData(
            mwFilters.asin,
            mwFilters.keyword,
            mwFilters.brand,
            {
              isExcludeSameBrand: mwFilters.isExcludeSameBrand,
              excludedBrands: mwFilters.excludedBrands,
              isExcludeSpreetailAsin: mwFilters.isExcludeSpreetailAsin,
            }
          );

          // Build filter summary
          const mwFetchParts: string[] = [];
          if (mwFilters.brand.length > 0) mwFetchParts.push(`Brand: ${mwFilters.brand.join(", ")}`);
          if (mwFilters.asin.length > 0) mwFetchParts.push(`ASIN: ${mwFilters.asin.join(", ")}`);
          if (mwFilters.keyword.length > 0)
            mwFetchParts.push(`Keyword: ${mwFilters.keyword.join(", ")}`);
          const mwFetchSummary =
            mwFetchParts.length > 0 ? `Applied Filters: ${mwFetchParts.join(" | ")}` : "";

          // Delete only the existing MW Raw Data sheet (extract timestamp from current sheet name)
          const mwOldTs = extractMWTimestamp(sheetName);
          if (mwOldTs) {
            try {
              const oldMWDataSheet = context.workbook.worksheets.getItem(`MW Raw Data ${mwOldTs}`);
              oldMWDataSheet.delete();
              await context.sync();
            } catch (_) {
              // Sheet may not exist
            }
          }

          // Create new MW Raw Data sheet with fresh data
          const mwNewTimestamp = generateTimestamp();
          await createDataSheet(context, mwFetchData, mwFetchSummary, mwNewTimestamp);

          // Tag new sheet with filter state custom property
          const mwNewDataSheet = context.workbook.worksheets.getItem(`${SHEET_PREFIXES.MW_RAW_DATA} ${mwNewTimestamp}`);
          mwNewDataSheet.customProperties.add(CUSTOM_PROPERTIES.MW_FILTERS, JSON.stringify(mwFilters));
          mwNewDataSheet.activate();
          await context.sync();

          // Persist filters for taskpane
          await saveMarketWatchFilters(MARKET_WATCH_SHEET, mwFilters);

          window.location.hash = "#/market-watch";
          await Office.addin.showAsTaskpane();

          // Clear loader flags
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
            await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
          } catch (_) {}
          break;
        }
        case DASHBOARD_TYPES.ADVERTISING:
          const { filterStateManager } = await import("../shared/filterStateManager");
          if (!filterStateManager.isInitialized()) {
            await filterStateManager.initialize();
          }

          // Show the same loader UI as dashboard generation while Fetch runs
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.ADVERTISING_DATA);
          } catch (_) {}

          const advertisingFilterState = await filterStateManager.hydrateAdvertisingFiltersForSheet(sheetName);
          console.log("advertisingFilterState", advertisingFilterState);

          // Create raw data sheet and get its name BEFORE calling handleAdvertisingRawData
          let advertisingRawDataSheet: Excel.Worksheet;
          const worksheets = context.workbook.worksheets;
          worksheets.load("items/name");
          await context.sync();

          let advertisingRawDataExists = false;
          worksheets.items.forEach((sheet) => {
            if (sheet.name === SHEET_NAMES.ADVERTISING_RAW_DATA) {
              advertisingRawDataExists = true;
              advertisingRawDataSheet = sheet;
            }
          });

          if (advertisingRawDataExists) {
            console.log("📝 Advertising Raw Data worksheet exists, deleting...");
            advertisingRawDataSheet!.delete();
            await context.sync();
          }

          console.log("➕ Creating new Advertising Raw Data worksheet...");
          advertisingRawDataSheet = worksheets.add(SHEET_NAMES.ADVERTISING_RAW_DATA);
          advertisingRawDataSheet.load("name, id");
          await context.sync();
          const advertisingRawDataSheetName = advertisingRawDataSheet.name;
          console.log("advertisingRawDataSheetName (created before data fetch)", advertisingRawDataSheetName);

          // Update filter state for the NEW raw data sheet with the filters from the dashboard
          await filterStateManager.updateAdvertisingFilterStateForSheet(advertisingRawDataSheetName, {
            startDate: advertisingFilterState.startDate || null,
            endDate: advertisingFilterState.endDate || null,
            brand: advertisingFilterState.brand || [],
            skus: advertisingFilterState.skus || [],
            channel: advertisingFilterState.channel || "all",
            keyword: advertisingFilterState.keyword || [],
            campaign: advertisingFilterState.campaign || [],
          });

          // Set a flag in OfficeRuntime.storage to signal UI to refresh filters
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.REFRESH_ADVERTISING_FILTERS, 'true');
          } catch (_) { /* no-op */ }

          await handleAdvertisingRawData(context, advertisingFilterState, advertisingRawDataSheet);

          // ✅ DO NOT modify dashboard filters when fetching raw data
          // Dashboard filters should persist independently and remain untouched
          
          try { await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DATA); } catch (_) { }

          console.log(`Current sheet before hash change: ${sheetName}`);
          window.location.hash = "#/campaign-performance";
          await Office.addin.showAsTaskpane();
          console.log(`Taskpane shown for route: #/campaign-performance`);
          
          // After showing taskpane, get the active sheet name again to see if it changed
          const activeSheetAfterTaskpane = context.workbook.worksheets.getActiveWorksheet();
          activeSheetAfterTaskpane.load("name");
          await context.sync();
          console.log(`Active sheet after taskpane shown: ${activeSheetAfterTaskpane.name}`);

          // Clear loader flags once taskpane is shown
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
            await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
          } catch (_) {}
          break;
        default:

          console.log("Fallback to default raw data");
          event.completed();
      }
    });
  } catch (error) {
    console.error("Error showing raw data:", error);
    generateCatchRawData(error);
  }
  // Signal that the command is complete
  event.completed();
}


async function handleSalesRawData(context: Excel.RequestContext, dateRange?: string | null) {
  console.log("🎯 Generating Sales Raw Data...");

  const rowData = await findSalesDataSheet(context);

  if (!rowData) {
    await generateSalesDashboard(context, null, dateRange, "Sales Raw Data");
  } else {
    const existingSheet = rowData;
    existingSheet.load("name");
    await context.sync();

    const existingSheetName = existingSheet.name;
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    const newSheetName = generateNewSheetName(existingSheetName, worksheets.items);
    console.log("Creating new sheet:", newSheetName);

    const workSheet = worksheets.add(newSheetName);
    workSheet.activate();
    await context.sync();
    await generateSalesDashboard(context, workSheet, dateRange, "Sales Raw Data");
  }
}

/**
 * Handles Inventory Raw Data generation
 */
async function handleInventoryRawData(context: Excel.RequestContext) {
  console.log("🎯 Generating Inventory Raw Data...");

  let inventoryRawDataSheet: Excel.Worksheet;
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  let inventoryRawDataExists = false;
  worksheets.items.forEach((sheet) => {
    if (sheet.name === SHEET_NAMES.INVENTORY_RAW_DATA) {
      inventoryRawDataExists = true;
      inventoryRawDataSheet = sheet;
    }
  });

  if (inventoryRawDataExists) {
    console.log("📝 Inventory Raw Data worksheet exists, deleting...");
    inventoryRawDataSheet!.delete();
    await context.sync();
  }

  console.log("➕ Creating new Inventory Raw Data worksheet...");
  inventoryRawDataSheet = worksheets.add(SHEET_NAMES.INVENTORY_RAW_DATA);
  inventoryRawDataSheet.activate();
  await context.sync();

  await createDashboardHeading(
    inventoryRawDataSheet,
    SHEET_NAMES.INVENTORY_RAW_DATA,
    context,
    DASHBOARD_TYPES.RAW_DATA,
    null,
    null,
    null
  );

  try {
    const { fetchMockInventoryData } = await import("../shared/mockData");
    const inventoryData = await fetchMockInventoryData();

    console.log("📊 Inventory data fetched:", inventoryData);

    if (inventoryData && inventoryData.length > 0) {
      const headers = [
        "ASIN", "FNSKU", "SKU", "Product Name", "Condition", "Total Supply Quantity",
        "Inbound Working Quantity", "Inbound Shipped Quantity", "Inbound Receiving Quantity",
        "Reserved Quantity", "Unfulfillable Quantity", "Fulfillable Quantity"
      ];

      // Add headers
      const lastCol = getExcelColumnLetter(headers.length);
      const headerRange = inventoryRawDataSheet.getRange(`A4:${lastCol}4`);
      headerRange.values = [headers];
      headerRange.format.fill.color = "#4472C4";
      headerRange.format.font.color = "white";
      headerRange.format.font.bold = true;

      // Prepare inventory data rows
      const inventoryRows = inventoryData.map((item: any) => [
        item.asin || "",
        item.fnSku || "",
        item.sku || "",
        item.productName || "",
        item.condition || "",
        item.totalSupplyQuantity ?? "",
        item.inboundWorkingQuantity ?? "",
        item.inboundShippedQuantity ?? "",
        item.inboundReceivingQuantity ?? "",
        item.reservedQuantity ?? "",
        item.unfulfillableQuantity ?? "",
        item.fulfillableQuantity ?? "",
      ]);

      // Add data rows
      if (inventoryRows.length > 0) {
        const lastCol = getExcelColumnLetter(headers.length);
        const dataRange = inventoryRawDataSheet.getRange(
          `A5:${lastCol}${4 + inventoryRows.length}`
        );
        dataRange.values = inventoryRows;

        // Auto-fit columns
        inventoryRawDataSheet
          .getRange(`A4:${lastCol}${4 + inventoryRows.length}`)
          .format.autofitColumns();
      }

      console.log(`✅ Added ${inventoryRows.length} inventory records to sheet`);
    } else {
      // Show placeholder if no data
      const placeholderRange = inventoryRawDataSheet.getRange("A4:D6");
      placeholderRange.merge();
      placeholderRange.values = [["No inventory data available"]];
      placeholderRange.format.font.size = 14;
      placeholderRange.format.font.bold = true;
      placeholderRange.format.horizontalAlignment = "Center";
      placeholderRange.format.verticalAlignment = "Center";
      placeholderRange.format.fill.color = "#F0F4F8";
    }
  } catch (error) {
    console.error("Error fetching inventory data:", error);

    // Show error message
    const errorRange = inventoryRawDataSheet.getRange("A4:D6");
    errorRange.merge();
    errorRange.values = [["Error loading inventory data. Please check your connection."]];
    errorRange.format.font.size = 14;
    errorRange.format.font.bold = true;
    errorRange.format.horizontalAlignment = "Center";
    errorRange.format.verticalAlignment = "Center";
    errorRange.format.fill.color = "#FEF2F2";
    errorRange.format.font.color = "#DC2626";
  }

  await context.sync();
}

/**
 * Handles Advertising Raw Data generation
 */
export async function handleAdvertisingRawData(
  context: Excel.RequestContext,
  advertisingFilterState?: AdvertisingFilterState,
  advertisingRawDataSheet?: Excel.Worksheet
) {
  console.log("🎯 Generating Advertising Raw Data...");

  let targetSheet: Excel.Worksheet;

  if (advertisingRawDataSheet) {
    targetSheet = advertisingRawDataSheet;
  } else {
    // Original logic for creating sheet if not provided (fallback)
    const worksheets = context.workbook.worksheets;
    worksheets.load("items/name");
    await context.sync();

    let advertisingRawDataExists = false;
    worksheets.items.forEach((sheet) => {
      if (sheet.name === SHEET_NAMES.ADVERTISING_RAW_DATA) {
        advertisingRawDataExists = true;
        targetSheet = sheet;
      }
    });

    if (advertisingRawDataExists) {
      console.log("📝 Advertising Raw Data worksheet exists, deleting...");
      targetSheet!.delete();
      await context.sync();
    }

    console.log("➕ Creating new Advertising Raw Data worksheet...");
    targetSheet = worksheets.add(SHEET_NAMES.ADVERTISING_RAW_DATA);
    targetSheet.load("name, id");
    await context.sync();
  }

  const advertisingRawDataSheetName = targetSheet.name;
  console.log("advertisingRawDataSheetName", advertisingRawDataSheetName);

  targetSheet.activate();
  await context.sync();

  await createDashboardHeading(
    targetSheet,
    SHEET_NAMES.ADVERTISING_RAW_DATA,
    context,
    DASHBOARD_TYPES.RAW_DATA,
    null,
    null,
    null
  );


  try {
    let filtersForApi: any = {};
    let appliedSummary: string = "";
    try {
      if (!filterStateManager.isInitialized()) {
        await filterStateManager.initialize();
      }
      let p = advertisingFilterState
        ? advertisingFilterState
        : await filterStateManager.hydrateAdvertisingFiltersForSheet(advertisingRawDataSheetName);

      if (p.startDate && p.endDate) {
        const start = typeof p.startDate === "string" ? p.startDate : p.startDate?.toISOString().slice(0, 10);
        const end = typeof p.endDate === "string" ? p.endDate : p.endDate?.toISOString().slice(0, 10);
        if (start && end) {
          filtersForApi.startDate = start;
          filtersForApi.endDate = end;
        }
      }
      console.log("filtersForApi", filtersForApi);
      if (p.brand && p.brand.length > 0) filtersForApi.brands = p.brand;
      if (p.skus && p.skus.length > 0) filtersForApi.asins = p.skus;
      if (p.channel && p.channel !== "all") filtersForApi.channels = [p.channel];
      if (p.keyword && p.keyword.length > 0) filtersForApi.keyword_contains = p.keyword;
      if (p.campaign && p.campaign.length > 0) filtersForApi.campaign_name = p.campaign;

      // Removed: filterStateManager.updateAdvertisingFilterStateForSheet is now called before this function
      // No need to update the filter state here, as it's already been set by showRawData

      const formatDateMMDDYYYY = (dateStr: string) => {
        const date = new Date(dateStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };
      const parts: string[] = [];
      if (p.keyword && p.keyword.length > 0)
        parts.push(`Keyword: ${Array.isArray(p.keyword) ? p.keyword.join(", ") : p.keyword}`);
      if (p.campaign && p.campaign.length > 0) parts.push(`Campaign: ${p.campaign.join(", ")}`);
      if (p.brand && p.brand.length > 0) parts.push(`Brand: ${p.brand.join(", ")}`);
      if (p.skus && p.skus.length > 0) parts.push(`ASIN/SKU: ${p.skus.join(", ")}`);
      if (p.startDate && p.endDate) {
        const startDate = formatDateMMDDYYYY(typeof p.startDate === "string" ? p.startDate : p.startDate?.toISOString());
        const endDate = formatDateMMDDYYYY(typeof p.endDate === "string" ? p.endDate : p.endDate?.toISOString());
        parts.push(`Dates: ${startDate} - ${endDate}`);
      }
      appliedSummary = parts.length > 0 ? `Filters: ${parts.join(" • ")}` : "";
    } catch (_) { }

    // Fetch filtered advertising data using current filters
    const { fetchFilteredAdvertisingData } = await import("../shared/mockData");
    const marketingData = await fetchFilteredAdvertisingData(filtersForApi);

    console.log("📊 Advertising data fetched (filtered):", marketingData);

    // Show applied filters summary below header (row 3), starting at A3 and left-aligned
    try {
      const summaryRange = targetSheet.getRange("A3:K3");
      summaryRange.merge();
      targetSheet.getRange("A3").values = [[appliedSummary]];
      summaryRange.format.font.size = 10;
      summaryRange.format.font.italic = true;
      summaryRange.format.font.color = "#666666";
      // Left-align starting from A
      summaryRange.format.horizontalAlignment = "Left";
      summaryRange.format.verticalAlignment = "Center";
      summaryRange.format.fill.color = "#F0F4F8";
    } catch (e) {
      // non-fatal
    }

    if (marketingData && marketingData.data && marketingData.data.length > 0) {
      // Get column headers from the API response
      const columns = marketingData.columns || [];
      const data = marketingData.data;

      // Add headers
      const lastCol = getExcelColumnLetter(columns.length);
      const headerRange = targetSheet.getRange(`A4:${lastCol}4`);
      headerRange.values = [columns];
      headerRange.format.fill.color = "#4472C4";
      headerRange.format.font.color = "white";
      headerRange.format.font.bold = true;

      // Add data rows
      if (data.length > 0) {
        const lastCol2 = getExcelColumnLetter(columns.length);

        // Force campaign_id to Text to prevent scientific notation/precision loss
        try {
          const idColIdx = columns.indexOf("campaign_id");
          if (idColIdx >= 0) {
            const idColLetter = getExcelColumnLetter(idColIdx + 1);
            targetSheet.getRange(`${idColLetter}:${idColLetter}`).numberFormat = [
              ["@"],
            ];
          }
        } catch (_) { }

        // Write in chunks to avoid payload limit errors on web/Mac
        await writeValuesInChunks(targetSheet, 5, columns.length, data, context);

        // Auto-fit columns
        targetSheet
          .getRange(`A4:${lastCol2}${4 + data.length}`)
          .format.autofitColumns();

        // Constrain columns E–J width specifically for Advertising Raw Data to avoid oversized title band
        try {
          ["E", "F", "G", "H", "I", "J", "K"].forEach(col => {
            targetSheet.getRange(`${col}:${col}`).format.columnWidth = 90;
          });
        } catch (_) { }
      }

      console.log(`✅ Added ${data.length} advertising records to sheet`);
    } else {
      // Show placeholder if no data
      const placeholderRange = targetSheet.getRange("A4:D6");
      placeholderRange.merge();
      placeholderRange.values = [["No advertising data available"]];
      placeholderRange.format.font.size = 14;
      placeholderRange.format.font.bold = true;
      placeholderRange.format.horizontalAlignment = "Center";
      placeholderRange.format.verticalAlignment = "Center";
      placeholderRange.format.fill.color = "#F0F4F8";
    }
  } catch (error) {
    console.error("Error fetching advertising data:", error);

    // Show error message
    const errorRange = targetSheet.getRange("A4:D6");
    errorRange.merge();
    errorRange.values = [["Error loading advertising data. Please check your connection."]];
    errorRange.format.font.size = 14;
    errorRange.format.font.bold = true;
    errorRange.format.horizontalAlignment = "Center";
    errorRange.format.verticalAlignment = "Center";
    errorRange.format.fill.color = "#FEF2F2";
    errorRange.format.font.color = "#DC2626";
  }

  await context.sync();
}

/**
 * Handles Sales Raw Data generation with preserve logic
 */
async function handleSalesRawDataPreserve(
  context: Excel.RequestContext,
  dateRange?: string | null
) {
  console.log("🎯 Generating Sales Raw Data (preserve)...");

  const rowData = await findSalesDataSheet(context);

  if (!rowData) {
    // Create new Sales Raw Data sheet
    await generateSalesDashboard(context, null, dateRange, "Sales Raw Data");
  } else {
    const copiedSheet = rowData.copy(Excel.WorksheetPositionType.after, rowData);
    await updateCopiedSheet(context, copiedSheet, dateRange);
  }
}

/**
 * Handles Inventory Raw Data generation with preserve logic
 */
async function handleInventoryRawDataPreserve(context: Excel.RequestContext) {
  console.log("🎯 Generating Inventory Raw Data (preserve)...");

  // For now, use the same logic as non-preserve since we don't have existing inventory data sheets
  await handleInventoryRawData(context);
}

/**
 * Handles Advertising Raw Data generation with preserve logic
 */
async function handleAdvertisingRawDataPreserve(context: Excel.RequestContext) {
  console.log("🎯 Generating Advertising Raw Data (preserve)...");

  // Find an existing Advertising Raw Data sheet
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  const items = worksheets.items;
  const advertisingDataSheet = items.find((ws) => SHEET_PATTERNS.ADVERTISING_RAW_DATA.test(ws.name));

  if (!advertisingDataSheet) {
    // No existing advertising data sheet: create fresh non-preserve
    // await handleAdvertisingRawData(context);
    return;
  }

  // Duplicate the existing sheet to preserve computations/formatting, then update the copy
  const copiedSheet = advertisingDataSheet.copy(
    Excel.WorksheetPositionType.after,
    advertisingDataSheet
  );
  await updateCopiedAdvertisingSheet(context, copiedSheet);
}

async function showRawDataPreserve(event: Office.AddinCommands.Event) {
  console.log("preserveCal function called from ribbon", event);

  try {
    await Excel.run(async (context) => {
      console.log("📊 Starting context-aware raw data display (preserve)...");

      const sheet = await findActiveSheet(context);
      const sheetName = sheet.name;
      console.log("Active sheet:", sheetName);

      // Determine dashboard type based on sheet name
      let dashboardType: string | null = null;
      let dateRange: string | null = null;

      if (sheetName === SHEET_NAMES.DASHBOARD) {
        dashboardType = DASHBOARD_TYPES.SALES;
        dateRange = await readDateRangeFromDashboard(context);
        console.log("Sales Dashboard detected, date range:", dateRange);
      } else if (sheetName === DASHBOARD_TYPES.INVENTORY) {
        dashboardType = DASHBOARD_TYPES.INVENTORY;
        console.log("Inventory Dashboard detected");
      } else if (
        sheetName === DASHBOARD_TYPES.MARKETING ||
        sheetName === DASHBOARD_TYPES.ADVERTISING ||
        sheetName === SHEET_TYPE_KEYWORDS.ADVERTISING_DASHBOARD ||
        sheetName.includes(SHEET_TYPE_KEYWORDS.ADVERTISING)
      ) {
        dashboardType = DASHBOARD_TYPES.ADVERTISING;
        console.log("Advertising Dashboard detected");
      } else if (sheetName.startsWith(SHEET_PREFIXES.MW)) {
        dashboardType = "MarketWatch";
        console.log("Market Watch sheet detected");
      } else {
        // Check if it's a raw data sheet
        const { isRawDataSheet } = await import("../taskpane/rowdata");
        if (isRawDataSheet(sheetName)) {
          // If we're on a raw data sheet, determine type from sheet name
          if (sheetName.includes(SHEET_TYPE_KEYWORDS.SALES)) {
            dashboardType = DASHBOARD_TYPES.SALES;
          } else if (sheetName.includes(SHEET_TYPE_KEYWORDS.INVENTORY)) {
            dashboardType = DASHBOARD_TYPES.INVENTORY;
          } else if (sheetName.includes(SHEET_TYPE_KEYWORDS.ADVERTISING)) {
            dashboardType = DASHBOARD_TYPES.ADVERTISING;
          }
          console.log("Raw data sheet detected, type:", dashboardType);
        } else {
          // No active dashboard - show no-dashboard page
          console.log("No active dashboard detected, showing no-dashboard page");
          // window.location.hash = "#/no-active-dashboard";
          await Office.addin.showAsTaskpane();
          event.completed();
          return;
        }
      }

      // For Advertising: open taskpane to new preserve-computation route (do not duplicate immediately)
      if (dashboardType === DASHBOARD_TYPES.ADVERTISING) {
        try { await OfficeRuntime.storage.setItem(STORAGE_KEYS.FILTER_UI_SOURCE, FILTER_UI_SOURCES.DATA); } catch (_) { }
        window.location.hash = "#/preserve-computation";
        await Office.addin.showAsTaskpane();
        return;
      }

      // For Market Watch: preserve old MW Raw Data sheet, create new one with fresh data
      if (dashboardType === "MarketWatch") {
        // Only preserve on MW Raw Data sheets — for Recommendations/Pivot/etc, open taskpane
        if (!sheetName.startsWith(SHEET_PREFIXES.MW_RAW_DATA)) {
          window.location.hash = "#/market-watch";
          await Office.addin.showAsTaskpane();
          return;
        }

        const { loadFiltersFromActiveMWSheet, saveMarketWatchFilters } =
          await import("../shared/marketWatchFilterState");
        const { fetchMarketWatchData } = await import("../shared/apiService");
        const { createDataSheet, generateTimestamp, readPreservedColumns, MARKET_WATCH_SHEET } =
          await import("../taskpane/components/Market-Watch");

        // Show loader
        try {
          await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
          await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.MARKET_WATCH_RAW_DATA);
        } catch (_) {}

        // Hydrate filters from active MW Raw Data sheet
        const mwPreserveFilters = await loadFiltersFromActiveMWSheet();
        if (
          !mwPreserveFilters ||
          (mwPreserveFilters.asin.length === 0 &&
            mwPreserveFilters.keyword.length === 0 &&
            mwPreserveFilters.brand.length === 0)
        ) {
          console.log("MW Preserve: No filters found, opening taskpane");
          window.location.hash = "#/market-watch";
          await Office.addin.showAsTaskpane();
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
            await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
          } catch (_) {}
          return;
        }

        // Fetch fresh data with same filters
        const mwPreserveData = await fetchMarketWatchData(
          mwPreserveFilters.asin,
          mwPreserveFilters.keyword,
          mwPreserveFilters.brand,
          {
            isExcludeSameBrand: mwPreserveFilters.isExcludeSameBrand,
            excludedBrands: mwPreserveFilters.excludedBrands,
            isExcludeSpreetailAsin: mwPreserveFilters.isExcludeSpreetailAsin,
          }
        );

        // Build filter summary
        const mwPreserveParts: string[] = [];
        if (mwPreserveFilters.brand.length > 0)
          mwPreserveParts.push(`Brand: ${mwPreserveFilters.brand.join(", ")}`);
        if (mwPreserveFilters.asin.length > 0)
          mwPreserveParts.push(`ASIN: ${mwPreserveFilters.asin.join(", ")}`);
        if (mwPreserveFilters.keyword.length > 0)
          mwPreserveParts.push(`Keyword: ${mwPreserveFilters.keyword.join(", ")}`);
        const mwPreserveSummary =
          mwPreserveParts.length > 0 ? `Applied Filters: ${mwPreserveParts.join(" | ")}` : "";

        // Read user-added columns from old sheet before creating the new one
        const oldSheet = context.workbook.worksheets.getActiveWorksheet();
        const preserved = await readPreservedColumns(context, oldSheet, mwPreserveData.headers);

        // Create NEW MW Raw Data sheet — old sheet is preserved untouched
        const mwPreserveTimestamp = generateTimestamp();
        await createDataSheet(
          context, mwPreserveData, mwPreserveSummary, mwPreserveTimestamp,
          preserved.headers.length > 0 ? preserved : undefined
        );

        // Tag new sheet with filter state custom property
        const mwPreserveNewSheet = context.workbook.worksheets.getItem(
          `${SHEET_PREFIXES.MW_RAW_DATA} ${mwPreserveTimestamp}`
        );
        mwPreserveNewSheet.customProperties.add(CUSTOM_PROPERTIES.MW_FILTERS, JSON.stringify(mwPreserveFilters));
        mwPreserveNewSheet.activate();
        await context.sync();

        // Persist filters for taskpane
        await saveMarketWatchFilters(MARKET_WATCH_SHEET, mwPreserveFilters);

        // Clear loader flags
        try {
          await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
          await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
        } catch (_) {}
        return;
      }

      // Others fallback to existing behavior for now
      switch (dashboardType) {
        case DASHBOARD_TYPES.SALES:
          await handleSalesRawDataPreserve(context, dateRange);
          break;
        case DASHBOARD_TYPES.INVENTORY:
          await handleInventoryRawDataPreserve(context);
          break;
        default:
          await geneateDefultRawData(context);
      }
    });
  } catch (error) {
    console.error("Error showing raw data:", error);
    generateCatchRawData(error);
  }
  // Signal that the command is complete
  event.completed();
}

/**
 * Refreshes the current dashboard based on which sheet is active
 * @param event
 */
async function refreshDataPlaceholder(event: Office.AddinCommands.Event) {
  const userAuthorized = await isAuthorized();
  if (!userAuthorized) {
    event.completed();
    return;
  }
  try {
    await Excel.run(async (context) => {
      // Get the currently active worksheet
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("name");
      await context.sync();

      const currentSheetName = activeSheet.name;
      console.log("Refresh - Current active sheet:", currentSheetName);

      if (currentSheetName === DASHBOARD_TYPES.INVENTORY) {
        console.log("Refreshing Inventory Dashboard...");
        await generateInventoryDashboard();
      } else if (currentSheetName === SHEET_NAMES.DASHBOARD) {
        console.log("Refreshing Sales Performance Dashboard...");
        const startCell = activeSheet.getRange("I3"); 
        const endCell = activeSheet.getRange("K3"); 
        startCell.load(["values", "text"]);
        endCell.load(["values", "text"]);
        await context.sync();

        let startText = "";
        let endText = "";

        try {
          startText =
            startCell.text && startCell.text[0] && startCell.text[0][0]
              ? String(startCell.text[0][0])
              : "";
          if (!startText) {
            startText =
              startCell.values && startCell.values[0] && startCell.values[0][0]
                ? String(startCell.values[0][0])
                : "";
          }
        } catch (e) {
          startText =
            startCell.values && startCell.values[0] && startCell.values[0][0]
              ? String(startCell.values[0][0])
              : "";
        }

        try {
          endText =
            endCell.text && endCell.text[0] && endCell.text[0][0] ? String(endCell.text[0][0]) : "";
          if (!endText) {
            endText =
              endCell.values && endCell.values[0] && endCell.values[0][0]
                ? String(endCell.values[0][0])
                : "";
          }
        } catch (e) {
          endText =
            endCell.values && endCell.values[0] && endCell.values[0][0]
              ? String(endCell.values[0][0])
              : "";
        }

        console.log("Refresh - Raw cell values:", { startText, endText });

        const toISO = (txt: string) => {
          if (!txt || txt === "undefined" || txt === "null") return "";
          try {
            const num = parseFloat(txt);
            if (!isNaN(num) && num > 10000) {
              const excelEpoch = new Date(1900, 0, 1);
              const date = new Date(excelEpoch.getTime() + (num - 2) * 24 * 60 * 60 * 1000);
              return date.toISOString();
            }

            const date = new Date(txt);
            if (isNaN(date.getTime())) {
              console.error("Invalid date:", txt);
              return "";
            }
            return date.toISOString();
          } catch (e) {
            console.error("Date parsing error for:", txt, e);
            return "";
          }
        };

        const startISO = toISO(startText);
        const endISO = toISO(endText);

        console.log("Refreshing with dates:", {
          startText,
          endText,
          startISO: startISO ? startISO.substring(0, 10) : "",
          endISO: endISO ? endISO.substring(0, 10) : "",
        });

        await createEnhancedSalesPerformanceDashboardWithParams({
          skus: [],
          start_purchase_date: startISO,
          end_purchase_date: endISO,
        });
      } else if (currentSheetName === SHEET_TYPE_KEYWORDS.ADVERTISING_DASHBOARD) {
        if (!filterStateManager.isInitialized()) {
          await filterStateManager.initialize();
        }
        const filters = await filterStateManager.hydrateAdvertisingFiltersForSheet(currentSheetName);
        const normalizedFilters = {
          startDate: filters.startDate ? (typeof filters.startDate === "string" ? filters.startDate : filters.startDate.toISOString().slice(0, 10)) : null,
          endDate: filters.endDate ? (typeof filters.endDate === "string" ? filters.endDate : filters.endDate.toISOString().slice(0, 10)) : null,
          brand: filters.brand || [],
          skus: filters.skus || [],
          channel: filters.channel || "",
          keyword: filters.keyword || [],
          campaign: filters.campaign || [],
        };
        const filteredData = await fetchFilteredAdvertisingData(normalizedFilters);
        await generateMarketingDashboard(filteredData);
      } else if (currentSheetName.startsWith(SHEET_PREFIXES.MW)) {
        // Market Watch Refresh: re-fetch data, detect changes, prompt for re-analysis
        const { loadFiltersFromActiveMWSheet } = await import("../shared/marketWatchFilterState");
        const {
          extractMWTimestamp, refreshMarketWatchDataOnly,
          MW_STALE_ANALYSIS_EVENT, MW_STALE_ANALYSIS_STORAGE_KEY,
        } = await import("../shared/marketWatchPipeline");

        const mwFilters = await loadFiltersFromActiveMWSheet();
        if (
          !mwFilters ||
          (mwFilters.asin.length === 0 &&
            mwFilters.keyword.length === 0 &&
            mwFilters.brand.length === 0)
        ) {
          console.log("MW Refresh: No filters found, opening taskpane");
          window.location.hash = "#/market-watch";
          await Office.addin.showAsTaskpane();
          event.completed();
          return;
        }

        // Show loader
        try {
          await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
          await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.MARKET_WATCH_DATA);
        } catch (_) {}

        const ts = extractMWTimestamp(currentSheetName) || "";

        try {
          const { dataChanged, analysisExists } = await refreshMarketWatchDataOnly(mwFilters, ts);

          // Prompt for re-analysis if data changed and analysis sheets exist
          if (dataChanged && ts && analysisExists) {
            const staleContext = JSON.stringify({ timestamp: ts, filters: mwFilters });
            await OfficeRuntime.storage.setItem(MW_STALE_ANALYSIS_STORAGE_KEY, staleContext);
            window.dispatchEvent(new CustomEvent(MW_STALE_ANALYSIS_EVENT));
            window.location.hash = "#/market-watch";
            await Office.addin.showAsTaskpane();
          }
        } finally {
          // Clear loader flags
          try {
            await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
            await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
          } catch (_) {}
        }
      } else {
        const { isRawDataSheet, isSalesDataSheet, refreshRawDataSheet, refreshSalesDataSheet } =
          await import("../taskpane/rowdata");
        if (isRawDataSheet(currentSheetName)) {
          console.log(`Ribbon: Refreshing data sheet: ${currentSheetName}...`);

          const dateRange = await readDateRangeFromDashboard(context);
          console.log("Date range for data refresh:", dateRange);

          if (isSalesDataSheet(currentSheetName)) {
            console.log("Refreshing Sales Data sheet (orders only)");
            await refreshSalesDataSheet(context, activeSheet, dateRange);
          } else if (isAdvertisingDataSheet(currentSheetName)) {
            console.log("Refreshing Advertising Data sheet");
            await refreshAdvertisingRawDataSheet(context, activeSheet, true);
          } else {
            console.log("Refreshing Raw Data sheet (products + orders)");
          }
        } else {
          console.log("Ribbon: Unknown sheet type - cannot refresh:", currentSheetName);
          Office.context.ui.displayDialogAsync(
            `data:text/html,
            <html>
              <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
                <h2 style="color: #dc3545;">⚠ Cannot Refresh</h2>
                <p>Cannot refresh "${currentSheetName}" sheet.</p>
                <p style="font-size: 12px; color: #666;">Please switch to a dashboard sheet (Dashboard, Inventory, or Raw Data) to refresh.</p>
                <button onclick="Office.context.ui.messageParent('close')" 
                        style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
                  Close
                </button>
              </body>
            </html>`,
            DIALOG_SIZES.MEDIUM
          );
          event.completed();
          return;
        }
      }
    });
  } catch (error) {
    console.log("Refresh invoked with error", error);
  }

  // Signal that the command is complete
  event.completed();
}

/**
 * Shows AI-powered insights and recommendations as a modal within Excel
 * Now supports filtering by selected SKUs in Excel
 * @param event
 */
async function showInsights(event: Office.AddinCommands.Event) {
  try {
    // First, try to extract data from Pivot Table (if selection is in one)
    let tableData = await extractPivotTableDataIfPresent();

    // If no Pivot Table data, fall back to regular table/selection extraction
    if (!tableData) {
      console.log("📊 No Pivot Table found, using standard data extraction");
      tableData = await extractSelectedData();
    } else {
      console.log("📊 Successfully extracted data from Pivot Table");
    }

    let selectedSKUs: string[] = [];

    // Extract SKUs if table data is available
    if (tableData && tableData.headers.length > 0 && tableData.rows.length > 0) {
      selectedSKUs = extractSKUs(tableData);
      console.log("Selected SKUs found (for display):", selectedSKUs.length);
    }

    // Store table data in sessionStorage if available
    let dataKey: string | null = null;
    if (tableData && tableData.headers.length > 0 && tableData.rows.length > 0) {
      dataKey = `ai-insights-data-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      try {
        sessionStorage.setItem(dataKey, JSON.stringify(tableData));
        console.log("Stored table data in sessionStorage:", {
          key: dataKey,
          headers: tableData.headers.length,
          rows: tableData.rows.length,
        });
      } catch (error) {
        console.error("Failed to store table data in sessionStorage:", error);
        dataKey = null;
      }
    }

    // Build the URL with parameters
    let dialogUrl = "https://localhost:3000/insights-dialog.html";
    const params = new URLSearchParams();
    params.append("user_prompt", DEFAULT_PROMPT);

    if (dataKey) {
      params.append("data_key", dataKey);
    } else {
      console.warn("No table data extracted from selection");
      params.append("no_data", "true");
      dialogUrl += "?" + params.toString();

      const url = new URL(dialogUrl);
      const taskpaneUrl = `/ai-insights${url.search}`;
      window.location.hash = `#${taskpaneUrl}`;
      await Office.addin.showAsTaskpane();
      event.completed();
      return;
    }

    // Add SKU parameters for display/debug only (optional)
    if (selectedSKUs.length > 0) {
      selectedSKUs.forEach((sku) => params.append("skus", sku));
      console.log("Number of SKUs found:", selectedSKUs.length);
    }

    dialogUrl += "?" + params.toString();

    // Save default prompt to history (non-blocking)
    saveDefaultPromptToHistory().catch((err) => {
      console.warn("Failed to save default prompt to history:", err);
    });

    try {
      const url = new URL(dialogUrl);
      const taskpaneUrl = `/ai-insights${url.search}`;
      window.location.hash = `#${taskpaneUrl}`;
      await Office.addin.showAsTaskpane();
    } catch (error) {
      console.error("Failed to navigate to taskpane:", error);
    }
  } catch (error) {
    console.error("Error in showInsights:", error);
  }

  event.completed();
}

/**
 * Loads saved dashboard templates and data
 * @param event
 */
async function loadData(event: Office.AddinCommands.Event) {
  try {
    // Check authorization
    const userAuthorized = await isAuthorized();
    if (!userAuthorized) {
      event.completed();
      return;
    }

    // Navigate to templates list UI
    window.location.hash = "#/templates";
    await Office.addin.showAsTaskpane();

  } catch (error) {
    console.error("Error opening templates:", error);
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #dc3545;">⚠ Error</h2>
          <p>Failed to load templates. Please try again.</p>
          <button onclick="Office.context.ui.messageParent('close')" 
                  style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  }

  event.completed();
}

/**
 * Saves current dashboard configuration and data using default Excel save behavior
 * @param event
 */
async function saveDataDefault(event: Office.AddinCommands.Event) {
  try {
    // Use Excel's native save functionality
    await Excel.run(async (context) => {
      // Get the active worksheet
      const sheet = context.workbook.worksheets.getActiveWorksheet();

      // Save the current workbook using Excel's built-in save
      await context.sync();

      // Show success message
      Office.context.ui.displayDialogAsync(
        `data:text/html,
        <html>
          <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
            <h2 style="color: #28a745;">✓ Saved Successfully</h2>
            <p>Your dashboard has been saved using Excel's default save behavior.</p>
            <p style="font-size: 12px; color: #666;">Last saved: ${new Date().toLocaleString()}</p>
            <button onclick="Office.context.ui.messageParent('close')" 
                    style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
              Close
            </button>
          </body>
        </html>`,
        DIALOG_SIZES.SMALL
      );
    });
  } catch (error) {
    console.error("Error saving data:", error);
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #dc3545;">⚠ Error</h2>
          <p>Failed to save dashboard. Please try again.</p>
          <button onclick="Office.context.ui.messageParent('close')"
                  style="background: #dc3545; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  }

  event.completed();
}

/**
 * Navigates to the Save Templates page in the taskpane
 * @param event
 */
async function saveData(event: Office.AddinCommands.Event) {
  try {
    // Detect active sheet to determine MW vs advertising flow
    const sheetName = await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load("name");
      await context.sync();
      return sheet.name;
    });

    if (sheetName.startsWith(SHEET_PREFIXES.MW)) {
      // Market Watch sheets — only allow save from MW Raw Data sheets
      if (!sheetName.startsWith(SHEET_PREFIXES.MW_RAW_DATA)) {
        Office.context.ui.displayDialogAsync(
          `data:text/html,
          <html>
            <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
              <h2 style="color: #dc3545;">Cannot Save Template</h2>
              <p>Templates can only be saved from MW Raw Data sheets.</p>
              <p>Please switch to an MW Raw Data sheet and try again.</p>
            </body>
          </html>`,
          DIALOG_SIZES.SMALL
        );
        event.completed();
        return;
      }

      // MW Raw Data sheet — navigate to save templates via taskpane
      console.log("MW Raw Data sheet detected — opening save templates in taskpane");
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.NAVIGATE_TO_SAVE_TEMPLATES, "true");
      await Office.addin.showAsTaskpane();
      event.completed();
      return;
    }

    // Non-MW sheets — existing advertising/sales/inventory flow
    console.log("Navigate to save templates page");
    await OfficeRuntime.storage.setItem(STORAGE_KEYS.NAVIGATE_TO_SAVE_TEMPLATES, "true");

    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #28a745;">Opening Save Templates</h2>
          <p>Redirecting to the Save Templates page...</p>
          <button onclick="Office.context.ui.messageParent('close')"
                  style="background: #28a745; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Close
          </button>
        </body>
      </html>`,
      DIALOG_SIZES.SMALL
    );
  } catch (error) {
    console.error("Error navigating to save templates:", error);
  }

  event.completed();
}

/**
 * Imports or exports data in various formats
 * @param event
 */
async function importExportData(event: Office.AddinCommands.Event) {
  try {
    Office.context.ui.displayDialogAsync(
      `data:text/html,
      <html>
        <body style="font-family: Segoe UI; padding: 20px; text-align: center;">
          <h2 style="color: #2196F3;">📤 Import/Export Data</h2>
          <p>Choose an operation:</p>
          <div style="margin: 20px 0;">
            <button onclick="importData()" 
                    style="background: #28a745; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 10px; width: 120px;">
              📥 Import
            </button>
            <button onclick="exportData()" 
                    style="background: #FF9800; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin: 10px; width: 120px;">
              📤 Export
            </button>
          </div>
          <button onclick="Office.context.ui.messageParent('close')" 
                  style="background: #6c757d; color: white; border: none; padding: 8px 16px; border-radius: 4px; cursor: pointer;">
            Cancel
          </button>
          <script>
            function importData() {
              Office.context.ui.messageParent(JSON.stringify({action: 'import'}));
            }
            function exportData() {
              Office.context.ui.messageParent(JSON.stringify({action: 'export'}));
            }
          </script>
        </body>
      </html>`,
      DIALOG_SIZES.LARGE
    );
  } catch (error) {
    console.error("Error opening import/export dialog:", error);
  }

  event.completed();
}

/**
 * Closes the insights modal by removing all modal-related shapes
 * @param event
 */
async function closeInsightsModal(event: Office.AddinCommands.Event) {
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      const shapes = sheet.shapes;
      shapes.load("items");
      await context.sync();

      // Remove all modal-related shapes
      const modalShapeNames = [
        "InsightsModalBackdrop",
        "InsightsModalContent",
        "InsightsModalTitle",
        "InsightsModalCloseButton",
        "InsightsModalCloseButtonText",
      ];

      shapes.items.forEach((shape) => {
        if (modalShapeNames.includes(shape.name)) {
          shape.delete();
        }
      });

      await context.sync();
    });
  } catch (error) {
    console.error("Error closing insights modal:", error);
  }

  // Signal that the command is complete
  event.completed();
}

/**
 * Modifies the AI prompt for custom insights generation
 * This is a placeholder function that can be implemented later
 * @param event
 */
async function modifyPrompt(event: Office.AddinCommands.Event) {
  // Navigate to edit prompt route using hash
  window.location.hash = "#/edit-prompt";
  await Office.addin.showAsTaskpane();

  // Signal that the command is complete
  event.completed();
}

async function createMarketingDashboard(event: Office.AddinCommands.Event) {
  console.log("createMarketingDashboard function called from ribbon", event);
  const userAuthorized = await isAuthorized();
  if (!userAuthorized) {
    event.completed();
    return;
  }
  try {
    try {
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "true");
      await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME, DASHBOARD_NAMES.ADVERTISING);
    } catch (_) { }

    // Create the Marketing (Advertising) dashboard in Excel
    await generateMarketingDashboard();

    // Navigate to Campaign Performance filter UI after generation
    window.location.hash = "#/campaign-performance";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  }
  // Clear loader
  try {
    await OfficeRuntime.storage.setItem(STORAGE_KEYS.DASHBOARD_GENERATING, "false");
    await OfficeRuntime.storage.removeItem(STORAGE_KEYS.DASHBOARD_GENERATING_NAME);
  } catch (_) { }
  event.completed();
}

/**
 * Smart Match functionality for data analysis
 * @param event
 */
async function smartMatch(event: Office.AddinCommands.Event) {
  console.log("smartMatch function called from ribbon", event);
  try {
    // Navigate to smart match route using hash
    window.location.hash = "#/smart-match";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  }
  event.completed();
}

/**
 * Smart Column functionality for data analysis
 * @param event
 */
async function smartColumn(event: Office.AddinCommands.Event) {
  console.log("smartColumn function called from ribbon", event);
  try {
    // Navigate to smart column route using hash
    window.location.hash = "#/smart-column";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  }
  event.completed();
}

async function aggregation(event: Office.AddinCommands.Event) {
  console.log("pivotTable function called from ribbon", event);
  try {
    // Navigate to pivot table route using hash
    window.location.hash = "#/pivot-table";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  } finally {
    event.completed();
  }
}

async function rootCauseAnalysis(event: Office.AddinCommands.Event) {
  console.log("rootCauseAnalysis function called from ribbon", event);
  try {
    // Navigate to root cause analysis route using hash
    window.location.hash = "#/root-cause-analysis";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  } finally {
    event.completed();
  }
}

async function createMarketWatchDashboard(event: Office.AddinCommands.Event) {
  console.log("createMarketWatchDashboard function called from ribbon", event);
  const userAuthorized = await isAuthorized();
  if (!userAuthorized) {
    event.completed();
    return;
  }
  try {
    window.location.hash = "#/market-watch";
    await Office.addin.showAsTaskpane();
  } catch (error) {
    console.log("error", error);
  } finally {
    event.completed();
  }
}

// Export the functions for use in other modules if needed
export {
  refreshAmazonData,
  createSalesPerformanceDashboard,
  createMarketingDashboard,
  smartMatch,
  smartColumn,
  aggregation,
  showRawData,
  refreshDataPlaceholder,
  showInsights,
  closeInsightsModal,
  loadData,
  saveDataDefault,
  saveData,
  modifyPrompt,
  showRawDataPreserve,
  createMarketWatchDashboard,
};
